import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, HeartHandshake, Activity, Globe2, ChevronRight, Check } from "lucide-react";
import api from "../../lib/api";

// First-run welcome — a short, skippable "What is WYVN" intro shown once so new users
// (and app reviewers) instantly understand the value. Stored in localStorage.
const SEEN_KEY = "wyvn_intro_seen";

export function hasSeenIntro() {
  try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return true; }
}

const SLIDES = [
  {
    icon: Sparkles,
    color: "#6AA6FF",
    title: "Welcome to WYVN",
    body: "Your whole wellness routine — woven into one calm, beautiful space. No clutter, just what moves you forward.",
  },
  {
    icon: HeartHandshake,
    color: "#E7A6C4",
    title: "Five coaches in your corner",
    body: "Chat with AI coaches for training, nutrition, mind, recovery and more — warm, personal guidance whenever you need it.",
  },
  {
    icon: Activity,
    color: "#7A9E7E",
    title: "Track everything, gently",
    body: "Workouts, meals, cycle, weight, water and daily habits — logged in seconds and shown as a story of your progress.",
  },
  {
    icon: Globe2,
    color: "#E7C069",
    title: "Win daily. Woven forever.",
    body: "Celebrate wins with a supportive community, shareable cards and your travel Passport. Let's begin.",
  },
];

export function WelcomeIntro({ onDone }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;

  const finish = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
    api.dismissNudge("intro").catch(() => {});
    onDone && onDone();
  };
  const next = () => (last ? finish() : setI((v) => v + 1));

  const s = SLIDES[i];
  const Icon = s.icon;

  return createPortal((
    <div className="fixed inset-0 z-[120] flex justify-center overflow-y-auto bg-ink/95 p-5 backdrop-blur-md" data-testid="welcome-intro">
      <div className="my-auto w-full max-w-md">
        <div
          className="glass animate-modal-pop relative overflow-hidden rounded-[2rem] border border-white/10 p-8 text-center"
          style={{ background: `radial-gradient(120% 80% at 50% 0%, ${s.color}26 0%, transparent 60%), rgba(255,255,255,0.02)` }}
        >
          <div
            key={i}
            className="animate-modal-pop mx-auto flex h-20 w-20 items-center justify-center rounded-3xl"
            style={{ background: `${s.color}1F`, color: s.color }}
          >
            <Icon size={34} />
          </div>
          <h2 key={`t-${i}`} className="animate-modal-pop mt-6 font-display text-3xl font-bold text-mist">{s.title}</h2>
          <p key={`b-${i}`} className="animate-modal-pop mx-auto mt-3 max-w-sm text-sm leading-relaxed text-mist-muted">{s.body}</p>

          <div className="mt-8 flex items-center justify-center gap-2" data-testid="welcome-dots">
            {SLIDES.map((_, idx) => (
              <span
                key={idx}
                className="h-1.5 rounded-full transition-all"
                style={{ width: idx === i ? 22 : 7, background: idx === i ? s.color : "rgba(255,255,255,0.18)" }}
              />
            ))}
          </div>

          <button
            onClick={next}
            data-testid="welcome-next-btn"
            className="btn-primary press mt-6 flex w-full items-center justify-center gap-2"
          >
            {last ? <>Get started <Check size={16} /></> : <>Next <ChevronRight size={16} /></>}
          </button>
        </div>

        {!last && (
          <button onClick={finish} data-testid="welcome-skip-btn" className="mx-auto mt-4 block text-xs font-medium text-mist-muted transition-colors hover:text-mist">
            Skip intro
          </button>
        )}
      </div>
    </div>
  ), document.body);
}
